import { createElement, type CSSProperties, type RefObject, type SyntheticEvent } from "react";

import type {
  PatientWorkspaceSummaryResponseV1,
  WorkforceSessionSummaryV1,
} from "@amplecare/contracts";
import { designTokens } from "@amplecare/design-tokens";
import {
  Image,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type LayoutChangeEvent,
} from "react-native";

import { styles } from "../AppShell.styles.js";
import { LineIcon, type LineIconName } from "../line-icons.js";
import {
  patientDesktopPalette,
  patientMobilePalette,
  type AppShellTheme,
} from "../shell-core.js";
import { createWebGradientBackgroundStyle } from "../shell-primitives.js";
import {
  legacyBackgroundTextureSource,
  renderPatientMobileChatAvatarMedia,
} from "./patient-assets.js";
import type { PatientOnboardingRegionKey } from "./patient-onboarding.js";
import {
  patientDesktopFooterLinks,
  type PatientSupportFormField,
  type PatientSupportFormState,
  type PatientSupportModalState,
} from "./patient-support.js";
import {
  type PatientWorkspaceReadAloudControls,
  type PatientWorkspaceVoiceInputControls,
} from "./patient-speech.js";
import type { ChatMessageViewModel } from "../types.js";
import { resolvePatientBrand } from "./brand.js";

const brand = resolvePatientBrand();

type PatientWorkspaceTabId =
  | "overview"
  | "onboarding"
  | "appointments"
  | "procedures"
  | "medications"
  | "documents"
  | "calls"
  | "messages"
  | "settings";

type PatientWorkspaceNavigationItem = {
  badgeText: string | null;
  id: PatientWorkspaceTabId;
  label: string;
};

type PatientWorkspaceActionableListItem = {
  detailRows: ReadonlyArray<{
    id: string;
    labelText: string;
    valueText: string;
  }>;
  detailSummaryText: string;
  id: string;
  subtitle?: string | null;
  supportingText?: string | null;
  title: string;
};

type PatientMedicationWorkspaceRow = {
  actionText?: string;
  id: string;
  labelText: string;
  noteText: string;
  routineText?: string;
  statusText?: string;
};

type PatientMessageCommandInput = {
  bodyText: string;
};

export type PatientMobileAuthStep = "phone" | "code";
export type PatientMobilePanelId = "terms" | "privacy" | "bug" | "feedback";
export type PatientMobileMedicationMode =
  | "alphabetical"
  | "morning"
  | "midday"
  | "evening";
export type PatientMobileDetailState =
  | {
      tabId: "appointments" | "procedures" | "medications" | "documents";
      itemId: string;
    }
  | null;

const patientMobileNavTabIds: readonly PatientWorkspaceTabId[] = [
  "overview",
  "appointments",
  "procedures",
  "medications",
  "documents",
  "calls",
];
export type PatientMedicationReminderSlotId = "morning" | "midday" | "evening";
export type PatientMedicationReminderPreferences = Record<
  PatientMedicationReminderSlotId,
  string
>;
export type PatientMedicationReminderTimeUpdateInput = {
  displayTime: string;
  label: string;
  slotId: PatientMedicationReminderSlotId;
  wakeUpTime: string;
};
const patientMedicationReminderSlots: readonly {
  fallbackWakeUpTime: string;
  id: PatientMedicationReminderSlotId;
  label: string;
  maxMinutes: number;
  minMinutes: number;
}[] = [
  {
    fallbackWakeUpTime: "07:00",
    id: "morning",
    label: "Morning",
    maxMinutes: 11 * 60,
    minMinutes: 5 * 60,
  },
  {
    fallbackWakeUpTime: "13:00",
    id: "midday",
    label: "Midday",
    maxMinutes: 16 * 60,
    minMinutes: 11 * 60,
  },
  {
    fallbackWakeUpTime: "21:00",
    id: "evening",
    label: "Evening",
    maxMinutes: 23 * 60,
    minMinutes: 17 * 60,
  },
];

function clampPatientPreferenceMinutes(
  value: number,
  minMinutes: number,
  maxMinutes: number,
) {
  return Math.min(maxMinutes, Math.max(minMinutes, value));
}

function roundPatientPreferenceMinutes(value: number) {
  return Math.round(value / 15) * 15;
}

function parsePatientPreferenceMinutes(
  value: string | null | undefined,
): number | null {
  const trimmedValue = value?.trim() ?? "";
  const match = /^(\d{1,2}):(\d{2})$/.exec(trimmedValue);

  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
}

function formatPatientPreferenceWakeUpTime(value: number): string {
  const minutes = roundPatientPreferenceMinutes(value);
  const hours24 = Math.floor(minutes / 60) % 24;
  const minuteValue = minutes % 60;

  return `${String(hours24).padStart(2, "0")}:${String(minuteValue).padStart(2, "0")}`;
}

function resolvePatientPreferenceWakeUpTime(
  value: string | null | undefined,
  fallbackWakeUpTime: string,
  minMinutes: number,
  maxMinutes: number,
) {
  const parsedValue = parsePatientPreferenceMinutes(value);
  const fallbackMinutes =
    parsePatientPreferenceMinutes(fallbackWakeUpTime) ?? minMinutes;
  const nextMinutes =
    parsedValue == null ||
    parsedValue < minMinutes ||
    parsedValue > maxMinutes
      ? fallbackMinutes
      : parsedValue;

  return formatPatientPreferenceWakeUpTime(
    clampPatientPreferenceMinutes(nextMinutes, minMinutes, maxMinutes),
  );
}

export function createPatientMedicationReminderPreferences(
  patientWakeUpTime?: string | null,
): PatientMedicationReminderPreferences {
  return patientMedicationReminderSlots.reduce(
    (preferences, slot) => ({
      ...preferences,
      [slot.id]: resolvePatientPreferenceWakeUpTime(
        slot.id === "morning" ? patientWakeUpTime : null,
        slot.fallbackWakeUpTime,
        slot.minMinutes,
        slot.maxMinutes,
      ),
    }),
    {} as PatientMedicationReminderPreferences,
  );
}

export function buildPatientMedicationReminderRows(
  preferences: PatientMedicationReminderPreferences,
) {
  return patientMedicationReminderSlots.map((slot) => {
    const wakeUpTime = resolvePatientPreferenceWakeUpTime(
      preferences[slot.id],
      slot.fallbackWakeUpTime,
      slot.minMinutes,
      slot.maxMinutes,
    );
    const minutes =
      parsePatientPreferenceMinutes(wakeUpTime) ??
      parsePatientPreferenceMinutes(slot.fallbackWakeUpTime) ??
      slot.minMinutes;

    return {
      ...slot,
      displayTime: formatPatientMobilePreferenceTime(wakeUpTime),
      minutes,
      wakeUpTime,
    };
  });
}

export function renderPatientMobileHeader(input: {
  layout: typeof designTokens.semanticLayout;
  onAskQuestion?: () => void;
  onOpenProfile?: () => void;
  onRegionLayout?: (
    regionKey: PatientOnboardingRegionKey,
    event: LayoutChangeEvent,
  ) => void;
  profileInitials?: string | null;
  showQuestionBar: boolean;
}) {
  const {
    layout,
    onAskQuestion,
    onOpenProfile,
    onRegionLayout,
    profileInitials,
    showQuestionBar,
  } = input;

  return (
    <View style={styles.patientMobileHeaderRegion}>
      <View
        style={[
          styles.patientMobileHeader,
          createWebGradientBackgroundStyle(patientMobilePalette.primaryGradient),
        ]}
      >
        <View
          accessibilityLabel={brand.brandName}
          style={styles.patientMobileHeaderBrand}
        >
          <Text
            numberOfLines={1}
            style={[
              styles.sectionTitle,
              {
                color: patientMobilePalette.white,
                fontFamily: layout.displayFontFamily,
                fontSize: 17,
                fontWeight: "800",
                lineHeight: 22,
              },
            ]}
          >
            {brand.brandName}
          </Text>
        </View>
      {onOpenProfile ? (
        <Pressable
          accessibilityLabel="Open profile and settings"
          accessibilityRole="button"
          onPress={onOpenProfile}
          style={[
            styles.patientMobileProfileButton,
            {
              backgroundColor:
                patientMobilePalette.onGradientAvatarBackground,
              borderColor: patientMobilePalette.onGradientAvatarBorder,
              borderRadius: 18,
            },
          ]}
        >
          <Text
            numberOfLines={1}
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.white,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                fontWeight: "800",
                textAlign: "center",
              },
            ]}
          >
            {profileInitials ?? "ME"}
          </Text>
        </Pressable>
      ) : null}
      </View>
      {showQuestionBar ? (
        <View
          onLayout={
            onRegionLayout
              ? (event) => onRegionLayout("agent", event)
              : undefined
          }
          style={styles.patientMobileQuestionDock}
        >
          <Pressable
            onPress={onAskQuestion}
            style={[
              styles.patientMobileQuestionBar,
              {
                backgroundColor: patientMobilePalette.white,
                borderColor: patientMobilePalette.line,
                borderRadius: designTokens.radius.xl,
                marginLeft: 14,
                shadowColor: patientMobilePalette.actionBlue,
                shadowOffset: { height: 4, width: 0 },
                shadowOpacity: 0.15,
                shadowRadius: 16,
              },
            ]}
          >
            <Text
              numberOfLines={2}
              style={[
                styles.metricLabel,
                {
                  color: patientMobilePalette.mutedText,
                  flex: 1,
                  flexShrink: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 14,
                  lineHeight: 18,
                  minWidth: 0,
                },
              ]}
            >
              Ask a question...
            </Text>
            <View
              style={[
                styles.inlinePill,
                {
                  alignItems: "center",
                  alignSelf: "center",
                  backgroundColor: patientMobilePalette.body,
                  borderColor: patientMobilePalette.body,
                  borderRadius: designTokens.radius.pill,
                  flexShrink: 0,
                  height: 28,
                  justifyContent: "center",
                  paddingHorizontal: 0,
                  paddingVertical: 0,
                  width: 28,
                },
              ]}
            >
              <LineIcon
                color={patientMobilePalette.actionBlue}
                name="microphone"
                size={16}
                strokeWidth={2}
              />
            </View>
          </Pressable>
          <Pressable
            onPress={onAskQuestion}
            style={[
              styles.patientMobileSendCircle,
              createWebGradientBackgroundStyle(patientMobilePalette.primaryGradient),
              {
                backgroundColor: patientMobilePalette.actionBlue,
                borderColor: "transparent",
                borderRadius: 10,
                marginRight: 14,
              },
            ]}
          >
            <LineIcon
              color={patientMobilePalette.white}
              name="chevron-right"
              size={18}
              strokeWidth={2.5}
            />
          </Pressable>
        </View>
      ) : null}
    </View>
  );
}

export function renderPatientMobileFooter(input: {
  layout: typeof designTokens.semanticLayout;
  onOpenFooterLink: (panelId: PatientMobilePanelId) => void;
}) {
  const { layout, onOpenFooterLink } = input;

  return (
    <View
      style={[
        styles.patientMobileFooter,
        createWebGradientBackgroundStyle(patientMobilePalette.shellGradient),
        { backgroundColor: patientMobilePalette.footerBlue },
      ]}
    >
      <Image
        resizeMode="cover"
        source={legacyBackgroundTextureSource}
        style={styles.patientMobileFooterTexture}
      />
      <Text
        style={[
          styles.patientMobileEmergencyText,
          { fontFamily: layout.bodyFontFamily },
        ]}
      >
        {"For emergencies, call 911 immediately.\nProceed to the nearest Emergency Room."}
      </Text>
      <View style={styles.patientMobileFooterMeta}>
        <Text
          style={[
            styles.metricLabel,
            {
              color: patientMobilePalette.footerMuted,
              fontFamily: layout.bodyFontFamily,
              fontSize: 10,
            },
          ]}
        >
          {brand.poweredByText ? `${brand.poweredByText} · ` : ""}(c) AmpleCare.ai - All rights reserved.
        </Text>
        <View style={styles.patientMobileFooterLinks}>
          {patientDesktopFooterLinks.map((link) => (
            <Pressable
              key={link.id}
              onPress={() => onOpenFooterLink(link.id)}
              style={styles.patientMobileFooterLink}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.white,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    fontWeight: "600",
                  },
                ]}
              >
                {link.labelText}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
    </View>
  );
}

export function renderPatientMobileBottomNav(input: {
  layout: typeof designTokens.semanticLayout;
  navItems: readonly PatientWorkspaceNavigationItem[];
  onNavigateTab: (tabId: PatientWorkspaceTabId) => void;
  onRegionLayout?: (
    regionKey: PatientOnboardingRegionKey,
    event: LayoutChangeEvent,
  ) => void;
  selectedPatientWorkspaceTabId: PatientWorkspaceTabId;
}) {
  const {
    layout,
    navItems,
    onNavigateTab,
    onRegionLayout,
    selectedPatientWorkspaceTabId,
  } = input;
  const mobileNavItems = navItems.filter((item) =>
    patientMobileNavTabIds.includes(item.id),
  );

  return (
    <View
      onLayout={
        onRegionLayout ? (event) => onRegionLayout("nav", event) : undefined
      }
      style={[
        styles.patientMobileBottomNav,
        { backgroundColor: patientMobilePalette.nav },
      ]}
    >
      {mobileNavItems.map((item) => {
        const isSelected = item.id === selectedPatientWorkspaceTabId;

        return (
          <Pressable
            accessibilityLabel={item.label}
            key={item.id}
            onPress={() => onNavigateTab(item.id)}
            style={[
              styles.patientMobileNavButton,
              {
                backgroundColor: isSelected
                  ? patientMobilePalette.softBlue
                  : "transparent",
                borderRadius: layout.cardRadius,
              },
            ]}
          >
            {renderPatientMobileNavIcon(item.id, isSelected)}
            <View style={{ minHeight: 12 }}>
              {resolvePatientMobileNavLabelLines(item).map((labelLine) => (
                <Text
                  key={`${item.id}-${labelLine}`}
                  numberOfLines={1}
                  style={[
                    styles.metricLabel,
                    {
                      color: isSelected
                        ? patientMobilePalette.actionBlue
                        : patientMobilePalette.mutedText,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 9,
                      fontWeight: "700",
                      lineHeight: 11,
                      textAlign: "center",
                    },
                  ]}
                >
                  {labelLine}
                </Text>
              ))}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}

function renderPatientMobileNavIcon(
  itemId: PatientWorkspaceTabId,
  isSelected: boolean,
) {
  const color = isSelected
    ? patientMobilePalette.actionBlue
    : patientMobilePalette.mutedText;
  const iconName: LineIconName =
    itemId === "overview"
      ? "home"
      : itemId === "appointments"
      ? "appointment"
      : itemId === "procedures"
        ? "procedure"
        : itemId === "medications"
          ? "medication"
          : itemId === "documents"
            ? "document"
            : itemId === "calls"
              ? "phone"
              : "user";

  return (
    <View
      style={[
        styles.patientMobileNavIcon,
        {
          borderRadius: designTokens.radius.md,
        },
      ]}
    >
      <LineIcon color={color} name={iconName} size={15} strokeWidth={2.3} />
    </View>
  );
}

function resolvePatientMobileNavLabelLines(
  item: PatientWorkspaceNavigationItem,
): string[] {
  if (item.id === "overview") {
    return ["Home"];
  }

  if (item.id === "appointments") {
    return ["Visits"];
  }

  if (item.id === "procedures") {
    return ["Care"];
  }

  if (item.id === "medications") {
    return ["Meds"];
  }

  if (item.id === "documents") {
    return ["Docs"];
  }

  return [item.label];
}

function resolvePatientMobileDocumentTypeLabel(labelText: string): string {
  const normalized = labelText.trim().toLowerCase();

  if (normalized.includes("consent")) {
    return "Consent";
  }

  if (
    normalized.includes("questionnaire") ||
    normalized.includes("screening") ||
    normalized.includes("asb")
  ) {
    return "Questionnaire";
  }

  if (
    normalized.includes("lab") ||
    normalized.includes("pathology") ||
    normalized.includes("mri") ||
    normalized.includes("report")
  ) {
    return "Lab result";
  }

  if (
    normalized.includes("post-procedure") ||
    normalized.includes("post procedure") ||
    normalized.includes("procedure note")
  ) {
    return "Post-procedure note";
  }

  return "Other";
}

export function renderPatientWorkspaceMobileAuthScreen(input: {
  authNotice: string | null;
  authStep: PatientMobileAuthStep;
  layout: typeof designTokens.semanticLayout;
  onChangeOtpCode: (value: string) => void;
  onChangePhoneNumber: (value: string) => void;
  onOpenFooterLink: (panelId: PatientMobilePanelId) => void;
  onRequestCode: () => void;
  onVerifyCode: () => void;
  otpCode: string;
  phoneNumber: string;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    authNotice,
    authStep,
    layout,
    onChangeOtpCode,
    onChangePhoneNumber,
    onOpenFooterLink,
    onRequestCode,
    onVerifyCode,
    otpCode,
    phoneNumber,
    summary,
    theme,
    tokens,
  } = input;
  const isCodeStep = authStep === "code";
  const phoneLast4 = summary?.onboarding.auth.phoneNumberLast4;

  return (
    <View
      style={[
        styles.patientMobileRoot,
        { backgroundColor: patientMobilePalette.white },
      ]}
    >
      {renderPatientMobileHeader({
        layout,
        showQuestionBar: false,
      })}
      <View style={styles.patientMobileAuthBody}>
        <Text
          style={[
            styles.surfaceTitle,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.displayFontFamily,
              fontSize: 28,
              lineHeight: 34,
              maxWidth: 320,
              textAlign: "center",
              width: "100%",
            },
          ]}
        >
          {`Welcome to ${brand.brandName}!`}
        </Text>
        <Text
          style={[
            styles.navigationText,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 16,
              fontWeight: "700",
              maxWidth: 320,
              textAlign: "center",
              width: "100%",
            },
          ]}
        >
          {isCodeStep
            ? `Please enter the 6-digit code for ${phoneLast4 ? `the phone ending in ${phoneLast4}` : "your phone number"}.`
            : "Please enter your phone number to continue."}
        </Text>
        <View
          style={[
            styles.patientMobileInlineForm,
            {
              alignItems: "stretch",
              flexDirection: "column",
              gap: designTokens.spacing.md,
              maxWidth: 320,
            },
          ]}
        >
          <TextInput
            keyboardType={isCodeStep ? "number-pad" : "phone-pad"}
            maxLength={isCodeStep ? 8 : 20}
            onChangeText={isCodeStep ? onChangeOtpCode : onChangePhoneNumber}
            onSubmitEditing={isCodeStep ? onVerifyCode : onRequestCode}
            placeholder={isCodeStep ? "Enter verification code" : "Phone number"}
            placeholderTextColor={patientMobilePalette.primaryText}
            style={[
              styles.patientMobileAuthInput,
              {
                backgroundColor: patientMobilePalette.inputBackground,
                borderRadius: 28,
                color: patientMobilePalette.primaryText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 17,
                minWidth: 0,
                width: "100%",
              },
            ]}
            value={isCodeStep ? otpCode : phoneNumber}
          />
          <Pressable
            onPress={isCodeStep ? onVerifyCode : onRequestCode}
            style={[
              styles.patientMobilePrimaryButton,
              {
                alignSelf: "stretch",
                backgroundColor: patientMobilePalette.actionBlue,
                borderColor: patientMobilePalette.actionBlue,
                borderRadius: 28,
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: patientMobilePalette.white,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 18,
                  fontWeight: "800",
                },
              ]}
            >
              Next
            </Text>
          </Pressable>
        </View>
        {isCodeStep ? (
          <Pressable onPress={onRequestCode}>
            <Text
              style={[
                styles.metricLabel,
                {
                  color: patientMobilePalette.actionBlue,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 13,
                  fontWeight: "800",
                  textAlign: "center",
                },
              ]}
            >
              Request a new code
            </Text>
          </Pressable>
        ) : null}
        {authNotice ? (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.info,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
                textAlign: "center",
              },
            ]}
          >
            {authNotice}
          </Text>
        ) : null}
      </View>
      {renderPatientMobileFooter({ layout, onOpenFooterLink })}
    </View>
  );
}

export function renderPatientWorkspaceMobileDisclaimerScreen(input: {
  layout: typeof designTokens.semanticLayout;
  onAcknowledge: () => void;
  onOpenFooterLink: (panelId: PatientMobilePanelId) => void;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const { layout, onAcknowledge, onOpenFooterLink, theme, tokens } = input;

  return (
    <View
      style={[
        styles.patientMobileRoot,
        { backgroundColor: patientMobilePalette.body },
      ]}
    >
      {renderPatientMobileHeader({ layout, showQuestionBar: false })}
      <ScrollView
        style={styles.patientMobileContentScroll}
        contentContainerStyle={[
          styles.patientMobileScreenContent,
          {
            gap: designTokens.spacing.lg,
            paddingHorizontal: 28,
            paddingVertical: 18,
          },
        ]}
      >
        <View
          style={[
            styles.patientMobilePanelCard,
            {
              backgroundColor: patientMobilePalette.white,
              borderColor: patientMobilePalette.line,
              borderRadius: designTokens.radius.md,
              gap: designTokens.spacing.md,
              padding: 18,
            },
          ]}
        >
          <Text
            style={[
              styles.sectionTitle,
              {
                color: patientMobilePalette.primaryText,
                fontFamily: layout.displayFontFamily,
                fontSize: 18,
                lineHeight: 22,
              },
            ]}
          >
            Disclaimer
          </Text>
          <Text
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.primaryText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                lineHeight: 17,
                textAlign: "center",
              },
            ]}
          >
            Please take a minute to read the following disclaimer and
            acknowledge it in order to use the platform.
          </Text>
          {[
            "You are solely responsible for the medical, health and personal information you provide on AmpleCare AI. The generative response of this website will depend upon the information you provide to your Agent on AmpleCare AI.",
            "The information provided on this website or by this AI service is for general and informative purposes only. Please do not consider the generated responses as professional medical advice, diagnosis or treatment.",
            "Please consult your physician or other qualified medical practitioners before starting any new treatment or discontinuing an existing treatment.",
            "Though all efforts have been made to ensure the accuracy of the content on this website, AmpleCare AI accepts no responsibility for the accuracy, completeness, usefulness or otherwise of the contents.",
          ].map((paragraph) => (
            <Text
              key={paragraph}
              style={[
                styles.sectionSubtitle,
                {
                  color: patientMobilePalette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              {paragraph}
            </Text>
          ))}
          <Pressable
            onPress={onAcknowledge}
            style={[
              styles.patientMobileWideButton,
              {
                backgroundColor: patientMobilePalette.actionBlue,
                borderColor: patientMobilePalette.actionBlue,
                borderRadius: designTokens.radius.md,
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: patientMobilePalette.white,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 14,
                  fontWeight: "800",
                  textAlign: "center",
                },
              ]}
            >
              I acknowledge and wish to continue
            </Text>
          </Pressable>
        </View>
      </ScrollView>
      {renderPatientMobileFooter({ layout, onOpenFooterLink })}
    </View>
  );
}

export function renderPatientWorkspaceMobilePanelShell(input: {
  formState: PatientSupportFormState;
  layout: typeof designTokens.semanticLayout;
  onChangeSupportField: (field: PatientSupportFormField, value: string) => void;
  onClosePanel: () => void;
  onOpenFooterLink: (panelId: PatientMobilePanelId) => void;
  onSubmitSupport: () => void;
  panelId: PatientMobilePanelId;
  supportModalState: PatientSupportModalState | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    formState,
    layout,
    onChangeSupportField,
    onClosePanel,
    onOpenFooterLink,
    onSubmitSupport,
    panelId,
    supportModalState,
    theme,
    tokens,
  } = input;

  return (
    <View
      style={[
        styles.patientMobileRoot,
        { backgroundColor: patientMobilePalette.body },
      ]}
    >
      {renderPatientMobileHeader({ layout, showQuestionBar: false })}
      <ScrollView
        style={styles.patientMobileContentScroll}
        contentContainerStyle={[
          styles.patientMobileScreenContent,
          {
            gap: 14,
            justifyContent: "flex-end",
            paddingHorizontal: designTokens.spacing.none,
            paddingTop: 18,
          },
        ]}
      >
        {renderPatientMobilePanelContent({
          formState,
          layout,
          onChangeSupportField,
          onClosePanel,
          onSubmitSupport,
          panelId,
          supportModalState,
          theme,
          tokens,
        })}
      </ScrollView>
      {renderPatientMobileFooter({ layout, onOpenFooterLink })}
    </View>
  );
}

export function renderPatientMobilePanelContent(input: {
  formState: PatientSupportFormState;
  layout: typeof designTokens.semanticLayout;
  onChangeSupportField: (field: PatientSupportFormField, value: string) => void;
  onClosePanel: () => void;
  onSubmitSupport: () => void;
  panelId: PatientMobilePanelId;
  supportModalState: PatientSupportModalState | null;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    formState,
    layout,
    onChangeSupportField,
    onClosePanel,
    onSubmitSupport,
    panelId,
    supportModalState,
    theme,
    tokens,
  } = input;

  if (panelId === "privacy" || panelId === "terms") {
    const isPrivacy = panelId === "privacy";
    const title = isPrivacy ? "Privacy Policy" : "AmpleCare - Terms of Use";
    const lead = isPrivacy
      ? "Your privacy matters to us. Take a moment to review how we protect your data."
      : "Before using the app, take a moment to review our Terms of Use.";
    const sections = isPrivacy
      ? [
          {
            heading: "1. Introduction",
            body: "We are responsible for maintaining and protecting the personal information under our control. We have designated individuals responsible for compliance with our privacy policy.",
          },
          {
            heading: "2. Identifying Purposes",
            body: "We collect, use and disclose personal information to provide the product or service you requested and to offer services we believe may be relevant.",
          },
          {
            heading: "3. Consent",
            body: "Knowledge and consent are required for collection, use or disclosure of personal information except where required or permitted by law.",
          },
        ]
      : [
          {
            heading: "1. Introduction",
            body: "The use of our website is subject to these terms and conditions. If you access and use this website, you accept and agree to be bound by them.",
          },
          {
            heading: "2. Use of our Website",
            body: "You agree to use our website for legitimate purposes and not for any illegal or unauthorized purpose, including violations of intellectual property or privacy law.",
          },
          {
            heading: "3. General Conditions",
            body: "We reserve the right to refuse service to anyone at any time and to make changes to the service when needed.",
          },
        ];

    return (
      <View
        style={[
          styles.patientMobilePanelCard,
          {
            backgroundColor: patientMobilePalette.white,
            borderColor: patientMobilePalette.line,
            borderRadius: 28,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            gap: designTokens.spacing.md,
            paddingBottom: 22,
            paddingHorizontal: 18,
            paddingTop: 10,
            shadowColor: patientMobilePalette.actionBlue,
            shadowOffset: { height: -8, width: 0 },
            shadowOpacity: 0.08,
            shadowRadius: 20,
          },
        ]}
      >
        {renderPatientMobileSheetHandle()}
        {renderPatientMobilePanelCloseButton({
          layout,
          onClosePanel,
        })}
        <Text
          style={[
            styles.sectionTitle,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.displayFontFamily,
              fontSize: 18,
              lineHeight: 22,
            },
          ]}
        >
          {title}
        </Text>
        <Text
          style={[
            styles.metricLabel,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 12,
              lineHeight: 17,
              textAlign: "center",
            },
          ]}
        >
          {lead}
        </Text>
        {sections.map((section) => (
          <View key={section.heading} style={{ gap: 6 }}>
            <Text
              style={[
                styles.navigationText,
                {
                  color: patientMobilePalette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 13,
                  fontWeight: "800",
                },
              ]}
            >
              {section.heading}
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: patientMobilePalette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  lineHeight: 17,
                },
              ]}
            >
              {section.body}
            </Text>
          </View>
        ))}
      </View>
    );
  }

  const isBugReport = panelId === "bug";
  const successText = formState.successText;
  const title = successText
    ? isBugReport
      ? "Thank you for your report!"
      : "Thank you for your feedback!"
    : isBugReport
      ? "AmpleCare Bug Report"
      : "AmpleCare Feedback Form";
  const intro = isBugReport
    ? "Help us improve the app by reporting the bug you encountered. Let us know what's not working."
    : "We value your feedback. Share your thoughts about the app to help us improve.";

  return (
    <View
      style={[
      styles.patientMobilePanelCard,
      {
        backgroundColor: patientMobilePalette.white,
        borderColor: patientMobilePalette.line,
        borderRadius: 28,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        gap: 14,
        minHeight: successText ? 360 : undefined,
        paddingBottom: 22,
        paddingHorizontal: 18,
        paddingTop: 10,
        shadowColor: patientMobilePalette.actionBlue,
        shadowOffset: { height: -8, width: 0 },
        shadowOpacity: 0.08,
        shadowRadius: 20,
      },
    ]}
  >
    {renderPatientMobileSheetHandle()}
    {renderPatientMobilePanelCloseButton({ layout, onClosePanel })}
      <Text
        style={[
          styles.sectionTitle,
          {
            color: patientMobilePalette.primaryText,
            fontFamily: layout.displayFontFamily,
            fontSize: 18,
            lineHeight: 22,
            textAlign: "center",
          },
        ]}
      >
        {title}
      </Text>
      {successText ? (
        <>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: patientMobilePalette.primaryText,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.md,
                lineHeight: tokens.typography.lineHeight.md,
                textAlign: "center",
              },
            ]}
          >
            {successText}
          </Text>
          <Pressable
            onPress={onClosePanel}
            style={[
              styles.patientMobileSmallButton,
              {
                alignSelf: "center",
                backgroundColor: patientMobilePalette.actionBlue,
                borderColor: patientMobilePalette.actionBlue,
                borderRadius: designTokens.radius["2xl"],
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: patientMobilePalette.white,
                  fontFamily: layout.bodyFontFamily,
                  fontWeight: "800",
                },
              ]}
            >
              Close
            </Text>
          </Pressable>
        </>
      ) : (
        <>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: patientMobilePalette.primaryText,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.md,
                lineHeight: tokens.typography.lineHeight.md,
                textAlign: "center",
              },
            ]}
          >
            {intro}
          </Text>
          {renderPatientMobileSupportField({
            field: "name",
            formState,
            labelText: "Your name or nickname",
            layout,
            onChangeSupportField,
            placeholderText: "Enter name",
          })}
          {renderPatientMobileSupportField({
            field: "emailAddress",
            formState,
            labelText: "Email address (if you'd like us to get back to you)",
            layout,
            onChangeSupportField,
            placeholderText: "Enter email",
          })}
          {isBugReport ? (
            <>
              {renderPatientMobileSupportField({
                field: "bugContext",
                formState,
                labelText: "What were you doing when you noticed the bug? *",
                layout,
                onChangeSupportField,
                placeholderText:
                  "I.e. I was trying to check my appointments",
              })}
              {renderPatientMobileSupportField({
                field: "bugImpact",
                formState,
                isMultiline: true,
                labelText: "How did the bug affect your experience? *",
                layout,
                onChangeSupportField,
                placeholderText:
                  "The appointment details did not show up and I had to call my doctor to make sure.",
              })}
            </>
          ) : (
            renderPatientMobileSupportField({
              field: "feedbackText",
              formState,
              isMultiline: true,
              labelText:
                supportModalState?.kind === "chat-feedback"
                  ? "Your feedback about this answer *"
                  : "Your feedback *",
              layout,
              onChangeSupportField,
              placeholderText: "Enter your feedback",
            })
          )}
          {formState.errorText ? (
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.criticalText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  textAlign: "center",
                },
              ]}
            >
              {formState.errorText}
            </Text>
          ) : null}
          <View style={[styles.patientMobileSupportActions, { gap: 10 }]}>
            <Pressable
              disabled={formState.isSubmitting}
              onPress={onClosePanel}
              style={[
                styles.patientMobileSmallButton,
                {
                  backgroundColor: patientDesktopPalette.agentBlue,
                  borderColor: patientDesktopPalette.agentBlue,
                  borderRadius: designTokens.radius["2xl"],
                  opacity: formState.isSubmitting ? 0.7 : 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: patientMobilePalette.white,
                    fontFamily: layout.bodyFontFamily,
                    fontWeight: "800",
                  },
                ]}
              >
                Cancel
              </Text>
            </Pressable>
            <Pressable
              disabled={formState.isSubmitting}
              onPress={onSubmitSupport}
              style={[
                styles.patientMobileSmallButton,
                {
                  backgroundColor: patientMobilePalette.actionBlue,
                  borderColor: patientMobilePalette.actionBlue,
                  borderRadius: designTokens.radius["2xl"],
                  opacity: formState.isSubmitting ? 0.7 : 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: patientMobilePalette.white,
                    fontFamily: layout.bodyFontFamily,
                    fontWeight: "800",
                  },
                ]}
              >
                {formState.isSubmitting ? "Submitting" : "Submit"}
              </Text>
            </Pressable>
          </View>
          <Text
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.primaryText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                lineHeight: 17,
                textAlign: "center",
              },
            ]}
          >
            We will never share your personal data with anyone. If provided, it
            will be used only to contact you and follow up on your feedback.
          </Text>
        </>
      )}
    </View>
  );
}

function renderPatientMobileSheetHandle() {
  return (
    <View
      style={{
        alignSelf: "center",
        backgroundColor: patientMobilePalette.line,
        borderRadius: 3,
        height: 5,
        marginBottom: designTokens.spacing.xs,
        width: 40,
      }}
    />
  );
}

function renderPatientMobilePanelCloseButton(input: {
  layout: typeof designTokens.semanticLayout;
  onClosePanel: () => void;
}) {
  const { layout, onClosePanel } = input;

  return (
    <Pressable
      accessibilityLabel="Close"
      onPress={onClosePanel}
      style={[
        styles.patientMobilePanelCloseButton,
        {
          backgroundColor: patientMobilePalette.actionBlue,
          borderColor: patientMobilePalette.actionBlue,
          borderRadius: designTokens.radius.lg,
        },
      ]}
    >
      <Text
        style={[
          styles.metricLabel,
          {
            color: patientMobilePalette.white,
            fontFamily: layout.bodyFontFamily,
            fontSize: 12,
            fontWeight: "800",
          },
        ]}
      >
        X
      </Text>
    </Pressable>
  );
}

function renderPatientMobileSupportField(input: {
  field: PatientSupportFormField;
  formState: PatientSupportFormState;
  isMultiline?: boolean;
  labelText: string;
  layout: typeof designTokens.semanticLayout;
  onChangeSupportField: (field: PatientSupportFormField, value: string) => void;
  placeholderText: string;
}) {
  const {
    field,
    formState,
    isMultiline = false,
    labelText,
    layout,
    onChangeSupportField,
    placeholderText,
  } = input;

  return (
    <View style={[styles.patientSupportField, { gap: 5 }]}>
      <Text
        style={[
          styles.metricLabel,
          {
            color: patientMobilePalette.primaryText,
            fontFamily: layout.bodyFontFamily,
            fontSize: 12,
            fontWeight: "800",
          },
        ]}
      >
        {labelText}
      </Text>
      <TextInput
        editable={!formState.isSubmitting}
        multiline={isMultiline}
        onChangeText={(value) => onChangeSupportField(field, value)}
        placeholder={placeholderText}
        placeholderTextColor={patientMobilePalette.actionBlue}
        style={[
          styles.patientMobileSupportInput,
          isMultiline ? styles.patientMobileSupportTextArea : null,
          {
            backgroundColor: patientMobilePalette.fieldBackground,
            borderColor: patientMobilePalette.line,
            borderRadius: 6,
            color: patientMobilePalette.primaryText,
            fontFamily: layout.bodyFontFamily,
            fontSize: 16,
          },
        ]}
        value={formState[field]}
      />
    </View>
  );
}

export function renderPatientMedicationPreferenceSlider(input: {
  activeColor: string;
  label: string;
  maxMinutes: number;
  minMinutes: number;
  minutes: number;
  onChange: (wakeUpTime: string) => void;
  onCommit: (wakeUpTime: string) => void;
  trackColor: string;
}) {
  const {
    activeColor,
    label,
    maxMinutes,
    minMinutes,
    minutes,
    onChange,
    onCommit,
    trackColor,
  } = input;
  const clampedMinutes = clampPatientPreferenceMinutes(
    roundPatientPreferenceMinutes(minutes),
    minMinutes,
    maxMinutes,
  );
  const progress =
    maxMinutes === minMinutes
      ? 0
      : ((clampedMinutes - minMinutes) / (maxMinutes - minMinutes)) * 100;
  const commitSliderValue = (value: number) => {
    const wakeUpTime = formatPatientPreferenceWakeUpTime(
      clampPatientPreferenceMinutes(value, minMinutes, maxMinutes),
    );

    onCommit(wakeUpTime);
  };
  const changeSliderValue = (value: number) => {
    const wakeUpTime = formatPatientPreferenceWakeUpTime(
      clampPatientPreferenceMinutes(value, minMinutes, maxMinutes),
    );

    onChange(wakeUpTime);
  };

  if (Platform.OS === "web") {
    const rangeStyle: CSSProperties = {
      accentColor: activeColor,
      background: `linear-gradient(to right, ${activeColor} 0%, ${activeColor} ${progress}%, ${trackColor} ${progress}%, ${trackColor} 100%)`,
      borderRadius: designTokens.radius.md,
      cursor: "pointer",
      height: 18,
      margin: designTokens.spacing.none,
      outline: "none",
      width: "100%",
    };
    const readEventValue = (event: SyntheticEvent<HTMLInputElement>) =>
      Number(event.currentTarget.value);

    return createElement("input", {
      "aria-label": `${label} medication reminder time`,
      max: maxMinutes,
      min: minMinutes,
      onBlur: (event: SyntheticEvent<HTMLInputElement>) =>
        commitSliderValue(readEventValue(event)),
      onChange: (event: SyntheticEvent<HTMLInputElement>) =>
        changeSliderValue(readEventValue(event)),
      onMouseUp: (event: SyntheticEvent<HTMLInputElement>) =>
        commitSliderValue(readEventValue(event)),
      onTouchEnd: (event: SyntheticEvent<HTMLInputElement>) =>
        commitSliderValue(readEventValue(event)),
      step: 15,
      style: rangeStyle,
      type: "range",
      value: clampedMinutes,
    });
  }

  return (
    <Pressable
      accessibilityLabel={`${label} medication reminder time`}
      accessibilityRole="adjustable"
      onPress={() => commitSliderValue(clampedMinutes)}
      style={[
        styles.patientMedicationPreferenceSliderFallback,
        {
          backgroundColor: trackColor,
          borderRadius: designTokens.radius.md,
        },
      ]}
    >
      <View
        style={{
          backgroundColor: activeColor,
          borderRadius: designTokens.radius.md,
          height: "100%",
          width: `${progress}%`,
        }}
      />
    </Pressable>
  );
}

export function renderPatientMobileSectionHeader(input: {
  layout: typeof designTokens.semanticLayout;
  onPress: () => void;
  title: string;
}) {
  const { layout, onPress, title } = input;

  return (
    <View style={styles.patientMobileSectionHeader}>
      <Text
        style={[
          styles.sectionTitle,
          {
            color: patientMobilePalette.primaryText,
            fontFamily: layout.displayFontFamily,
            fontSize: 18,
            lineHeight: 22,
          },
        ]}
      >
        {title}
      </Text>
      <Pressable
        accessibilityLabel={`See all ${title.toLowerCase()}`}
        accessibilityRole="button"
        onPress={onPress}
        style={[
          styles.patientRowFooter,
          {
            backgroundColor: patientMobilePalette.softBlue,
            borderColor: patientMobilePalette.softBlue,
            borderRadius: designTokens.radius.pill,
            borderWidth: 1,
            flexWrap: "nowrap",
            gap: 6,
            height: 30,
            justifyContent: "center",
            paddingLeft: 11,
            paddingRight: 6,
            paddingVertical: designTokens.spacing.none,
          },
        ]}
      >
        <Text
          style={[
            styles.metricLabel,
            {
              color: patientMobilePalette.actionBlue,
              fontFamily: layout.bodyFontFamily,
              fontSize: 11,
              fontWeight: "800",
              lineHeight: 14,
              textAlign: "center",
            },
          ]}
        >
          See all
        </Text>
        <View
          style={{
            alignItems: "center",
            backgroundColor: patientMobilePalette.white,
            borderRadius: designTokens.radius.pill,
            height: 17,
            justifyContent: "center",
            width: 17,
          }}
        >
          <LineIcon
            color={patientMobilePalette.actionBlue}
            name="chevron-right"
            size={10}
            strokeWidth={2.5}
          />
        </View>
      </Pressable>
    </View>
  );
}

export function renderPatientMobileActionListScreen(input: {
  detailState: PatientMobileDetailState;
  detailTabId: "appointments" | "procedures";
  emptyText: string;
  items: PatientWorkspaceActionableListItem[];
  layout: typeof designTokens.semanticLayout;
  onBackFromDetail: () => void;
  onOpenDetail: (detail: PatientMobileDetailState) => void;
  onReviewItem: (
    tabId: "appointments" | "procedures",
    item: PatientWorkspaceActionableListItem,
  ) => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  title: string;
}) {
  const {
    detailState,
    detailTabId,
    emptyText,
    items,
    layout,
    onBackFromDetail,
    onOpenDetail,
    onReviewItem,
    summary,
    title,
  } = input;
  const selectedItem =
    detailState?.tabId === detailTabId
      ? (items.find((item) => item.id === detailState.itemId) ?? items[0])
      : null;

  if (selectedItem) {
    return renderPatientMobileDetailScreen({
      item: selectedItem,
      layout,
      onBack: onBackFromDetail,
      onReviewItem,
      summary,
      tabId: detailTabId,
    });
  }

  return (
    <View style={[styles.workspaceGrid, { gap: 10 }]}>
      <Text
        style={[
          styles.sectionTitle,
          {
            color: patientMobilePalette.primaryText,
            fontFamily: layout.displayFontFamily,
            fontSize: 20,
            lineHeight: 24,
          },
        ]}
      >
        {title}
      </Text>
      {items.length > 0 ? (
        items.map((item, index) =>
          renderPatientMobileListRow({
            item,
            index,
            layout,
            onPress: () =>
              onOpenDetail({
                itemId: item.id,
                tabId: detailTabId,
              }),
            thumbnailText:
              detailTabId === "appointments"
                ? index % 3 === 0
                  ? "VIS"
                  : index % 3 === 1
                    ? "PHY"
                    : "LAB"
                : index % 3 === 0
                  ? "APP"
                  : index % 3 === 1
                    ? "CAB"
                    : "SUR",
          }),
        )
      ) : (
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: patientMobilePalette.mutedText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 14,
              lineHeight: 20,
            },
          ]}
        >
          {emptyText}
        </Text>
      )}
    </View>
  );
}

export function renderPatientMobileListRow(input: {
  index: number;
  item: Pick<
    PatientWorkspaceActionableListItem,
    "id" | "subtitle" | "supportingText" | "title"
  >;
  layout: typeof designTokens.semanticLayout;
  onPress: () => void;
  thumbnailText: string;
}) {
  const { item, layout, onPress, thumbnailText } = input;
  const rowIcon = resolvePatientMobileListRowIconName({
    thumbnailText,
    title: item.title,
  });
  const rowColor =
    rowIcon === "lab"
      ? patientMobilePalette.statusWarningText
      : rowIcon === "clinician"
        ? patientMobilePalette.statusSuccessText
        : rowIcon === "procedure"
          ? patientMobilePalette.actionBlue
          : rowIcon === "document"
            ? patientMobilePalette.statusInfoText
            : patientMobilePalette.actionBlue;
  const rowBackground =
    rowIcon === "lab"
      ? patientMobilePalette.statusWarningBackground
      : rowIcon === "clinician"
        ? patientMobilePalette.successSoft
        : rowIcon === "procedure"
          ? patientMobilePalette.procedureSoft
          : patientMobilePalette.actionSoft;

  return (
    <Pressable
      key={item.id}
      onPress={onPress}
      style={[
        styles.patientMobileListRow,
        {
          backgroundColor: "transparent",
          borderBottomColor: "rgba(14,165,233,.08)",
          borderRadius: designTokens.radius.none,
          paddingVertical: designTokens.spacing.md,
        },
      ]}
    >
      <View
        style={[
          styles.patientMobileRowThumb,
          {
            backgroundColor: rowBackground,
            borderRadius: 14,
          },
        ]}
      >
        <LineIcon color={rowColor} name={rowIcon} size={18} strokeWidth={2.25} />
      </View>
      <View style={[styles.sectionChromeCopy, { gap: 3 }]}>
        <Text
          numberOfLines={2}
          style={[
            styles.navigationText,
            {
              color: patientMobilePalette.actionBlue,
              fontFamily: layout.bodyFontFamily,
              fontSize: 13,
              fontWeight: "800",
              lineHeight: 16,
            },
          ]}
        >
          {item.title}
        </Text>
        <Text
          numberOfLines={2}
          style={[
            styles.metricLabel,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 11,
              lineHeight: 15,
            },
          ]}
        >
          {item.subtitle ?? item.supportingText ?? "Details are available."}
        </Text>
      </View>
    </Pressable>
  );
}

function resolvePatientMobileListRowIconName(input: {
  thumbnailText: string;
  title: string;
}): LineIconName {
  const normalized = `${input.thumbnailText} ${input.title}`.toLowerCase();

  if (normalized.includes("lab") || normalized.includes("pathology")) {
    return "lab";
  }

  if (
    normalized.includes("physician") ||
    normalized.includes("nurse") ||
    normalized.includes("clinician") ||
    normalized.includes("phy")
  ) {
    return "clinician";
  }

  if (
    normalized.includes("procedure") ||
    normalized.includes("surgery") ||
    normalized.includes("egd") ||
    normalized.includes("sur")
  ) {
    return "procedure";
  }

  if (
    normalized.includes("document") ||
    normalized.includes("record") ||
    normalized.includes("doc")
  ) {
    return "document";
  }

  return "appointment";
}

function renderPatientMobileDetailScreen(input: {
  item: PatientWorkspaceActionableListItem;
  layout: typeof designTokens.semanticLayout;
  onBack: () => void;
  onReviewItem: (
    tabId: "appointments" | "procedures",
    item: PatientWorkspaceActionableListItem,
  ) => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  tabId: "appointments" | "procedures";
}) {
  const { item, layout, onBack, onReviewItem, summary, tabId } = input;
  const isProcedure = tabId === "procedures";
  const reviewLabel = isProcedure
    ? "Review procedure with AI"
    : "Review appointment with AI";

  return (
    <View style={[styles.workspaceGrid, { gap: 10 }]}>
      {renderPatientMobileBackButton({ layout, onBack })}
      <View
        style={[
          styles.patientMobilePanelCard,
          {
            backgroundColor: patientMobilePalette.white,
            borderColor: patientMobilePalette.line,
            borderRadius: designTokens.radius.md,
            gap: designTokens.spacing.md,
            padding: designTokens.spacing.md,
          },
        ]}
      >
        <View style={[styles.patientMobileListRow, { gap: designTokens.spacing.md }]}>
          <View
            style={[
              styles.patientMobileRowThumb,
              {
                backgroundColor: isProcedure ? patientMobilePalette.procedureSoft : patientMobilePalette.softBlue,
                borderRadius: designTokens.radius.md,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: patientMobilePalette.actionBlue,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11,
                  fontWeight: "800",
                },
              ]}
            >
              {isProcedure ? "SUR" : "VIS"}
            </Text>
          </View>
          <View style={[styles.sectionChromeCopy, { gap: 3 }]}>
            <Text
              style={[
                styles.navigationText,
                {
                  color: patientMobilePalette.actionBlue,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 15,
                  fontWeight: "800",
                },
              ]}
            >
              {item.title}
            </Text>
            <Text
              style={[
                styles.metricLabel,
                {
                  color: patientMobilePalette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11,
                  lineHeight: 15,
                },
              ]}
            >
              {item.subtitle ?? summary?.nextAppointmentText ?? "Details pending"}
            </Text>
          </View>
        </View>
        <Text
          style={[
            styles.navigationText,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 13,
              fontWeight: "800",
            },
          ]}
        >
          {isProcedure ? "Procedure details" : "Appointment details"}
        </Text>
        <Pressable
          accessibilityLabel={reviewLabel}
          onPress={() => onReviewItem(tabId, item)}
          style={[
            styles.patientMobilePrimaryButton,
            {
              backgroundColor: patientMobilePalette.actionBlue,
              borderColor: patientMobilePalette.actionBlue,
              borderRadius: designTokens.radius.md,
              paddingHorizontal: 14,
              paddingVertical: designTokens.spacing.md,
            },
          ]}
        >
          <Text
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.white,
                fontFamily: layout.bodyFontFamily,
                fontSize: 13,
                fontWeight: "800",
                textAlign: "center",
              },
            ]}
          >
            {reviewLabel}
          </Text>
        </Pressable>
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 12,
              lineHeight: 17,
            },
          ]}
        >
          {item.detailSummaryText}
        </Text>
        {item.detailRows.map((row) => (
          <View key={row.id} style={[styles.workspaceGrid, { gap: designTokens.spacing.xs }]}>
            <Text
              style={[
                styles.metricLabel,
                {
                  color: patientMobilePalette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  fontWeight: "800",
                },
              ]}
            >
              {row.labelText}
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: patientMobilePalette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  lineHeight: 17,
                },
              ]}
            >
              {row.valueText}
            </Text>
          </View>
        ))}
        <View
          style={[
            styles.patientMobileMapMock,
            {
              backgroundColor: patientMobilePalette.softBlue,
              borderColor: patientMobilePalette.line,
              borderRadius: designTokens.radius.md,
            },
          ]}
        >
          <Text
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.actionBlue,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                fontWeight: "800",
                textAlign: "center",
              },
            ]}
          >
            Click or tap on the map to see your location
          </Text>
        </View>
      </View>
    </View>
  );
}

function renderPatientMobileBackButton(input: {
  layout: typeof designTokens.semanticLayout;
  onBack: () => void;
}) {
  const { layout, onBack } = input;

  return (
    <Pressable onPress={onBack} style={styles.patientMobileBackButton}>
      <Text
        style={[
          styles.navigationText,
          {
            color: patientMobilePalette.primaryText,
            fontFamily: layout.bodyFontFamily,
            fontSize: 15,
            fontWeight: "800",
          },
        ]}
      >
        {"<- Back"}
      </Text>
    </Pressable>
  );
}

export function renderPatientMobileMedicationsScreen(input: {
  detailState: PatientMobileDetailState;
  layout: typeof designTokens.semanticLayout;
  medicationMode: PatientMobileMedicationMode;
  onBackFromDetail: () => void;
  onChangeMedicationMode: (value: PatientMobileMedicationMode) => void;
  onOpenDetail: (detail: PatientMobileDetailState) => void;
  onReviewMedication: (row: PatientMedicationWorkspaceRow) => void;
  rows: readonly PatientMedicationWorkspaceRow[];
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
}) {
  const {
    detailState,
    layout,
    medicationMode,
    onBackFromDetail,
    onChangeMedicationMode,
    onOpenDetail,
    onReviewMedication,
    rows,
    summary,
  } = input;
  const filteredRows =
    medicationMode === "alphabetical"
      ? rows
      : rows.filter((_: PatientMedicationWorkspaceRow, index: number) =>
          medicationMode === "morning"
            ? index % 3 !== 2
            : medicationMode === "midday"
              ? index % 3 === 1
              : index % 3 !== 1,
        );
  const selectedMedication =
    detailState?.tabId === "medications"
      ? (rows.find((row) => row.id === detailState.itemId) ?? rows[0] ?? null)
      : null;

  if (selectedMedication) {
    const detailRows = [
      ["Schedule", selectedMedication.routineText ?? "Medication record"],
      ["Status", selectedMedication.statusText ?? summary?.statusLabel ?? "Active"],
      ["Instructions", selectedMedication.noteText],
      ["Care team", summary?.coordinatorLabel ?? "Not assigned"],
    ];

    return (
      <View style={[styles.workspaceGrid, { gap: 10 }]}>
        {renderPatientMobileBackButton({ layout, onBack: onBackFromDetail })}
        <View
          style={[
            styles.patientMobilePanelCard,
            {
              backgroundColor: patientMobilePalette.white,
              borderColor: patientMobilePalette.line,
              borderRadius: designTokens.radius.md,
              gap: designTokens.spacing.md,
              padding: 14,
            },
          ]}
        >
          <Text
            style={[
              styles.sectionTitle,
              {
                color: patientMobilePalette.actionBlue,
                fontFamily: layout.displayFontFamily,
                fontSize: 19,
                lineHeight: 23,
              },
            ]}
          >
            {selectedMedication.labelText}
          </Text>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: patientMobilePalette.primaryText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                lineHeight: 18,
              },
            ]}
          >
            {selectedMedication.noteText}
          </Text>
          <Pressable
            accessibilityLabel={`Review ${selectedMedication.labelText} with AI`}
            onPress={() => onReviewMedication(selectedMedication)}
            style={[
              styles.patientMobilePrimaryButton,
              {
                backgroundColor: patientMobilePalette.actionBlue,
                borderColor: patientMobilePalette.actionBlue,
                borderRadius: designTokens.radius.md,
                paddingHorizontal: 14,
                paddingVertical: designTokens.spacing.md,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: patientMobilePalette.white,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 13,
                  fontWeight: "800",
                  textAlign: "center",
                },
              ]}
            >
              Review medication with AI
            </Text>
          </Pressable>
          <View
            style={[
              styles.patientMobileDocumentTextBox,
              {
                backgroundColor: patientMobilePalette.fieldBackground,
                borderColor: patientMobilePalette.line,
                borderRadius: 6,
                padding: designTokens.spacing.md,
              },
            ]}
          >
            {detailRows.map(([labelText, valueText]) => (
              <View
                key={labelText}
                style={[
                  styles.patientRowHeader,
                  {
                    borderBottomColor: patientMobilePalette.line,
                    borderBottomWidth: 1,
                    paddingVertical: 7,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: patientMobilePalette.actionBlue,
                      flex: 1,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      fontWeight: "800",
                    },
                  ]}
                >
                  {labelText}
                </Text>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: patientMobilePalette.primaryText,
                      flex: 1.4,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      lineHeight: 17,
                      textAlign: "right",
                    },
                  ]}
                >
                  {valueText}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.workspaceGrid, { gap: 10 }]}>
      <View style={styles.patientMobileSectionHeader}>
        <Text
          style={[
            styles.sectionTitle,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.displayFontFamily,
              fontSize: 20,
              lineHeight: 24,
            },
          ]}
        >
          Your current medications
        </Text>
        <View style={[styles.patientRowFooter, { gap: 6 }]}>
          {(["alphabetical", "morning"] as const).map((mode) => (
            <Pressable key={mode} onPress={() => onChangeMedicationMode(mode)}>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color:
                      medicationMode === mode
                        ? patientMobilePalette.primaryText
                        : patientMobilePalette.mutedText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    fontWeight: "800",
                  },
                ]}
              >
                {mode === "alphabetical" ? "Alphabetical" : "Schedule"}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
      <View
        style={[
          styles.patientMobileSegmented,
          {
            backgroundColor: patientMobilePalette.white,
            borderColor: patientMobilePalette.line,
            borderRadius: designTokens.radius.md,
          },
        ]}
      >
        {(["morning", "midday", "evening"] as const).map((mode) => (
          <Pressable
            key={mode}
            onPress={() => onChangeMedicationMode(mode)}
            style={[
              styles.patientMobileSegment,
              {
                backgroundColor:
                  medicationMode === mode
                    ? patientMobilePalette.line
                    : patientMobilePalette.white,
                borderRadius: 7,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: patientMobilePalette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  fontWeight: "800",
                  textTransform: "capitalize",
                },
              ]}
            >
              {mode}
            </Text>
          </Pressable>
        ))}
      </View>
      {filteredRows.map((row: PatientMedicationWorkspaceRow) => (
        <Pressable
          key={row.id}
          onPress={() =>
            onOpenDetail({
              itemId: row.id,
              tabId: "medications",
            })
          }
          style={[
            styles.patientMobileMedicationRow,
            {
              backgroundColor: patientMobilePalette.white,
              borderColor: patientMobilePalette.line,
              borderRadius: designTokens.radius.md,
              padding: designTokens.spacing.md,
            },
          ]}
        >
          <View style={[styles.patientRowHeader, { gap: designTokens.spacing.sm }]}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: patientMobilePalette.primaryText,
                  flex: 1,
                  fontFamily: layout.displayFontFamily,
                  fontSize: 18,
                  lineHeight: 22,
                },
              ]}
            >
              {row.labelText}
            </Text>
            <Text
              style={[
                styles.metricLabel,
                {
                  color: patientMobilePalette.mutedText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                },
              ]}
            >
              500mg
            </Text>
          </View>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: patientMobilePalette.primaryText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                lineHeight: 17,
              },
            ]}
          >
            {row.noteText}
          </Text>
          <View style={[styles.patientRowFooter, { gap: designTokens.spacing.sm }]}>
            <View
              style={[
                styles.patientMobileTinyPill,
                {
                  backgroundColor: patientMobilePalette.softBlue,
                  borderColor: patientMobilePalette.softBlue,
                  borderRadius: designTokens.radius.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.actionBlue,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    fontWeight: "800",
                    textTransform: "uppercase",
                  },
                ]}
              >
                Take with food
              </Text>
            </View>
            <View style={styles.patientMobileDoseStack}>
              {[1, 2, 3].map((dose) => (
                <Text
                  key={dose}
                  style={[
                    styles.metricLabel,
                    {
                      backgroundColor: patientMobilePalette.detailBackground,
                      color: patientMobilePalette.primaryText,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11,
                      paddingHorizontal: designTokens.spacing.sm,
                      paddingVertical: designTokens.spacing.xxs,
                    },
                  ]}
                >
                  1 pill
                </Text>
              ))}
            </View>
          </View>
        </Pressable>
      ))}
    </View>
  );
}

export function renderPatientMobileDocumentsScreen(input: {
  detailState: PatientMobileDetailState;
  isUploadSubmitting: boolean;
  layout: typeof designTokens.semanticLayout;
  onBackFromDetail: () => void;
  onDownloadDocument: (
    document: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number],
  ) => void;
  onOpenDetail: (detail: PatientMobileDetailState) => void;
  onReviewDocument: (
    document: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number],
  ) => void;
  onUploadDocument: () => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
}) {
  const {
    detailState,
    isUploadSubmitting,
    layout,
    onBackFromDetail,
    onDownloadDocument,
    onOpenDetail,
    onReviewDocument,
    onUploadDocument,
    summary,
  } = input;
  const documents = summary?.documentSummaries ?? [];
  const selectedDocument =
    detailState?.tabId === "documents"
      ? (documents.find((document) => document.documentId === detailState.itemId) ??
        documents[0] ??
        null)
      : null;

  if (selectedDocument) {
    return renderPatientMobileDocumentDetail({
      document: selectedDocument,
      layout,
      onBack: onBackFromDetail,
      onDownloadDocument,
      onReviewDocument,
    });
  }

  return (
    <View style={[styles.workspaceGrid, { gap: 10 }]}>
      <View style={styles.patientMobileSectionHeader}>
        <Text
          style={[
            styles.sectionTitle,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.displayFontFamily,
              fontSize: 20,
              lineHeight: 24,
            },
          ]}
        >
          Your documents
        </Text>
      </View>
      <Pressable
        accessibilityLabel="Upload document"
        disabled={isUploadSubmitting}
        onPress={onUploadDocument}
        style={[
          styles.patientMobilePrimaryButton,
          {
            backgroundColor: isUploadSubmitting
              ? patientMobilePalette.actionSoft
              : patientMobilePalette.actionBlue,
            borderColor: isUploadSubmitting
              ? patientMobilePalette.line
              : patientMobilePalette.actionBlue,
            borderRadius: designTokens.radius.md,
            opacity: isUploadSubmitting ? 0.8 : 1,
            paddingHorizontal: 14,
            paddingVertical: designTokens.spacing.md,
          },
        ]}
      >
        <Text
          style={[
            styles.metricLabel,
            {
              color: isUploadSubmitting
                ? patientMobilePalette.actionBlue
                : patientMobilePalette.white,
              fontFamily: layout.bodyFontFamily,
              fontSize: 13,
              fontWeight: "800",
              textAlign: "center",
            },
          ]}
        >
          {isUploadSubmitting ? "Uploading document" : "Upload document"}
        </Text>
      </Pressable>
      {documents.length > 0 ? (
        documents.map((document, index) => (
          <Pressable
            key={document.documentId}
            onPress={() =>
              onOpenDetail({
                itemId: document.documentId,
                tabId: "documents",
              })
            }
            style={[
              styles.patientMobileDocumentRow,
              {
                backgroundColor: patientMobilePalette.white,
                borderColor: patientMobilePalette.line,
                borderRadius: designTokens.radius.md,
                padding: designTokens.spacing.md,
              },
            ]}
          >
            <View style={[styles.patientRowHeader, { gap: designTokens.spacing.sm }]}>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: patientMobilePalette.actionBlue,
                    flex: 1,
                    fontFamily: layout.displayFontFamily,
                    fontSize: 17,
                    fontWeight: "800",
                  },
                ]}
              >
                {document.label}
              </Text>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.actionBlue,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 16,
                    fontWeight: "800",
                  },
                ]}
              >
                ...
              </Text>
            </View>
            <Text
              numberOfLines={2}
              style={[
                styles.sectionSubtitle,
                {
                  color: patientMobilePalette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  lineHeight: 17,
                },
              ]}
            >
              {document.summaryText}
            </Text>
            {document.notes?.[0]?.bodyText ? (
              <Text
                numberOfLines={2}
                style={[
                  styles.sectionSubtitle,
                  {
                    color: patientMobilePalette.primaryText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    lineHeight: 17,
                  },
                ]}
              >
                {document.notes?.[0]?.bodyText}
              </Text>
            ) : null}
            <View style={styles.patientRowHeader}>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.primaryText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    fontWeight: "800",
                  },
                ]}
              >
                {document.createdAt.slice(0, 10)}
              </Text>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.primaryText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    fontWeight: "800",
                  },
                ]}
              >
                {document.createdByLabel}
              </Text>
            </View>
          </Pressable>
        ))
      ) : (
        renderPatientMobileListRow({
          index: 0,
          item: {
            id: "documents-empty",
            title: "No documents yet",
            subtitle: "Documents will appear here when they are available.",
            supportingText: null,
          },
          layout,
          onPress: () => undefined,
          thumbnailText: "DOC",
        })
      )}
    </View>
  );
}

function renderPatientMobileDocumentDetail(input: {
  document: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number];
  layout: typeof designTokens.semanticLayout;
  onBack: () => void;
  onDownloadDocument: (
    document: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number],
  ) => void;
  onReviewDocument: (
    document: PatientWorkspaceSummaryResponseV1["patient"]["documentSummaries"][number],
  ) => void;
}) {
  const { document, layout, onBack, onDownloadDocument, onReviewDocument } = input;
  const documentTypeLabel = resolvePatientMobileDocumentTypeLabel(
    document.label,
  );
  const availabilityLabel =
    document.availability === "download-ready"
      ? "Download ready"
      : "Metadata only";
  const detailRows = [
    ["Type", documentTypeLabel],
    ["Status", document.statusLabel],
    ["Availability", availabilityLabel],
    ["Source", document.createdByLabel],
    ["Created", document.createdAt.slice(0, 16).replace("T", " ")],
  ];

  return (
    <View style={[styles.workspaceGrid, { gap: 10 }]}>
      {renderPatientMobileBackButton({ layout, onBack })}
      <View
        style={[
          styles.patientMobilePanelCard,
          {
            backgroundColor: patientMobilePalette.white,
            borderColor: patientMobilePalette.line,
            borderRadius: designTokens.radius.md,
            gap: designTokens.spacing.md,
            padding: 14,
          },
        ]}
      >
        <View style={styles.patientRowHeader}>
          <Text
            style={[
              styles.sectionTitle,
              {
                color: patientMobilePalette.actionBlue,
                flex: 1,
                fontFamily: layout.displayFontFamily,
                fontSize: 19,
                lineHeight: 23,
              },
            ]}
          >
            {document.label}
          </Text>
          <Text
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.primaryText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 11,
                fontWeight: "800",
              },
            ]}
          >
            {document.createdByLabel}
          </Text>
        </View>
        <Text
          style={[
            styles.metricLabel,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 12,
              fontWeight: "800",
            },
          ]}
        >
          {document.createdAt.slice(0, 16).replace("T", " ")}
        </Text>
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 12,
              lineHeight: 18,
            },
          ]}
        >
          {document.summaryText}
        </Text>
        {document.notes?.[0]?.bodyText ? (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: patientMobilePalette.primaryText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                lineHeight: 18,
              },
            ]}
          >
            {document.notes?.[0]?.bodyText}
          </Text>
        ) : null}
        {document.availability === "download-ready" ? (
          <Pressable
            accessibilityLabel={`Download ${document.label}`}
            onPress={() => onDownloadDocument(document)}
            style={[
              styles.patientMobilePrimaryButton,
              {
                backgroundColor: patientMobilePalette.actionSoft,
                borderColor: patientMobilePalette.line,
                borderRadius: designTokens.radius.md,
                paddingHorizontal: 14,
                paddingVertical: designTokens.spacing.md,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: patientMobilePalette.actionBlue,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 13,
                  fontWeight: "800",
                  textAlign: "center",
                },
              ]}
            >
              Download PDF
            </Text>
          </Pressable>
        ) : null}
        <Pressable
          accessibilityLabel={`Review ${document.label} with AI`}
          onPress={() => onReviewDocument(document)}
          style={[
            styles.patientMobilePrimaryButton,
            {
              backgroundColor: patientMobilePalette.actionBlue,
              borderColor: patientMobilePalette.actionBlue,
              borderRadius: designTokens.radius.md,
              paddingHorizontal: 14,
              paddingVertical: designTokens.spacing.md,
            },
          ]}
        >
          <Text
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.white,
                fontFamily: layout.bodyFontFamily,
                fontSize: 13,
                fontWeight: "800",
                textAlign: "center",
              },
            ]}
          >
            Review with AI
          </Text>
        </Pressable>
        <View
          style={[
            styles.patientMobileDocumentTextBox,
            {
              backgroundColor: patientMobilePalette.fieldBackground,
              borderColor: patientMobilePalette.line,
              borderRadius: 6,
              padding: designTokens.spacing.md,
            },
          ]}
        >
          {detailRows.map(([labelText, valueText]) => (
            <View
              key={labelText}
              style={[
                styles.patientRowHeader,
                {
                  borderBottomColor: patientMobilePalette.line,
                  borderBottomWidth: 1,
                  paddingVertical: 7,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.actionBlue,
                    flex: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: "800",
                  },
                ]}
              >
                {labelText}
              </Text>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: patientMobilePalette.primaryText,
                    flex: 1.4,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    lineHeight: 17,
                    textAlign: "right",
                  },
                ]}
              >
                {valueText}
              </Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

export function renderPatientMobileSettingsScreen(input: {
  activeWorkforceSession: WorkforceSessionSummaryV1 | null;
  canSwitchWorkspaces: boolean;
  layout: typeof designTokens.semanticLayout;
  medicationReminderPreferences: PatientMedicationReminderPreferences;
  noticeText: string | null;
  onChangeReminderTime: (
    slotId: PatientMedicationReminderSlotId,
    wakeUpTime: string,
  ) => void;
  onSetRemindersEnabled: (enabled: boolean) => void;
  onSignOut: () => void;
  onStartOnboarding: () => void;
  onUpdateReminderTime: (
    input: PatientMedicationReminderTimeUpdateInput,
  ) => void;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
}) {
  const {
    activeWorkforceSession,
    canSwitchWorkspaces,
    layout,
    medicationReminderPreferences,
    noticeText,
    onChangeReminderTime,
    onSetRemindersEnabled,
    onSignOut,
    onStartOnboarding,
    onUpdateReminderTime,
    summary,
  } = input;
  const wakeRows = buildPatientMedicationReminderRows(
    medicationReminderPreferences,
  );
  const reminderRow = wakeRows[0] ?? null;
  const remindersEnabled = summary?.onboarding.preferences.remindersEnabled !== false;
  const formatProfileDate = (value: string | null | undefined) => {
    const trimmedValue = value?.trim() ?? "";
    const match = /^(\d{4})-(\d{2})-(\d{2})/u.exec(trimmedValue);

    if (!match) {
      return trimmedValue.length > 0 ? trimmedValue : null;
    }

    const [, yearText, monthText, dayText] = match;
    const year = Number.parseInt(yearText ?? "", 10);
    const month = Number.parseInt(monthText ?? "", 10);
    const day = Number.parseInt(dayText ?? "", 10);

    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return trimmedValue;
    }

    return new Intl.DateTimeFormat("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(Date.UTC(year, month - 1, day)));
  };
  const readProfileText = (
    value: string | null | undefined,
    fallback = "Not recorded",
  ) => {
    const trimmedValue = value?.trim() ?? "";

    return trimmedValue.length > 0 ? trimmedValue : fallback;
  };
  const formattedDateOfBirth = formatProfileDate(summary?.dateOfBirth);
  const identityRows = [
    {
      label: "Legal name",
      value: readProfileText(summary?.legalName ?? summary?.displayName, "Patient"),
    },
    ...(formattedDateOfBirth
      ? [{ label: "Date of birth", value: formattedDateOfBirth }]
      : []),
    {
      label: "System patient ID",
      value: readProfileText(summary?.identifier),
    },
    ...(summary?.medicalRecordNumber?.trim()
      ? [{ label: "MRN", value: summary.medicalRecordNumber.trim() }]
      : []),
  ];
  const careRows = [
    {
      label: "Care coordinator",
      value: readProfileText(summary?.coordinatorLabel, "Not assigned"),
    },
    {
      label: "Primary program",
      value: readProfileText(summary?.programLabel),
    },
    {
      label: "Next appointment",
      value: readProfileText(summary?.nextAppointmentText),
    },
    {
      label: "Contact window",
      value: readProfileText(summary?.contactWindowText),
    },
  ];
  const renderFactRow = (row: { label: string; value: string }) => (
    <View
      key={row.label}
      style={[
        styles.patientMobilePanelCard,
        {
          backgroundColor: patientMobilePalette.white,
          borderColor: patientMobilePalette.line,
          borderRadius: designTokens.radius.md,
          gap: designTokens.spacing.xxs,
          paddingHorizontal: designTokens.spacing.md,
          paddingVertical: 10,
        },
      ]}
    >
      <Text
        style={[
          styles.metricLabel,
          {
            color: patientMobilePalette.actionBlue,
            fontFamily: layout.bodyFontFamily,
            fontSize: 11,
            fontWeight: "800",
            lineHeight: 14,
          },
        ]}
      >
        {row.label}
      </Text>
      <Text
        style={[
          styles.navigationText,
          {
            color: patientMobilePalette.primaryText,
            fontFamily: layout.bodyFontFamily,
            fontSize: 15,
            fontWeight: "800",
            lineHeight: 20,
          },
        ]}
      >
        {row.value}
      </Text>
    </View>
  );

  return (
    <View style={[styles.workspaceGrid, { gap: 10 }]}>
      <Text
        style={[
          styles.sectionTitle,
          {
            color: patientMobilePalette.primaryText,
            fontFamily: layout.displayFontFamily,
            fontSize: 20,
            lineHeight: 24,
          },
        ]}
      >
        Profile
      </Text>
      <Text
        style={[
          styles.sectionSubtitle,
          {
            color: patientMobilePalette.actionBlue,
            fontFamily: layout.bodyFontFamily,
            fontSize: 13,
            lineHeight: 18,
          },
        ]}
      >
        Patient identity, reminder preferences, and care team.
      </Text>
      {noticeText ? (
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: patientMobilePalette.actionBlue,
              fontFamily: layout.bodyFontFamily,
              fontSize: 12,
              lineHeight: 17,
            },
          ]}
        >
          {noticeText}
        </Text>
      ) : null}
      <View
        style={[
          styles.patientMobilePanelCard,
          {
            backgroundColor: patientMobilePalette.white,
            borderColor: patientMobilePalette.line,
            borderRadius: designTokens.radius.md,
            gap: 14,
            padding: designTokens.spacing.lg,
          },
        ]}
      >
        <Text
          style={[
            styles.navigationText,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 14,
              fontWeight: "800",
            },
          ]}
        >
          Identifiers
        </Text>
        {identityRows.map(renderFactRow)}
      </View>
      <View
        style={[
          styles.patientMobilePanelCard,
          {
            backgroundColor: patientMobilePalette.white,
            borderColor: patientMobilePalette.line,
            borderRadius: designTokens.radius.md,
            gap: 14,
            padding: designTokens.spacing.lg,
          },
        ]}
      >
        <Text
          style={[
            styles.navigationText,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 14,
              fontWeight: "800",
            },
          ]}
        >
          Preferences
        </Text>
          <Pressable
            onPress={() => onSetRemindersEnabled(!remindersEnabled)}
            style={[
              styles.patientRowHeader,
              {
                backgroundColor: patientMobilePalette.actionSoft,
                borderColor: patientMobilePalette.line,
                borderRadius: designTokens.radius.md,
                borderWidth: 1,
                gap: designTokens.spacing.md,
                padding: designTokens.spacing.md,
              },
            ]}
          >
            <View
              style={[
                styles.patientMobileToggle,
                {
                  backgroundColor: remindersEnabled
                    ? patientMobilePalette.actionBlue
                    : patientMobilePalette.softBlue,
                  borderColor: remindersEnabled
                    ? patientMobilePalette.actionBlue
                    : patientMobilePalette.line,
                  borderRadius: designTokens.radius.lg,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: remindersEnabled
                      ? patientMobilePalette.white
                      : patientMobilePalette.primaryText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    fontWeight: "800",
                  },
                ]}
              >
                {remindersEnabled ? "ON" : "OFF"}
              </Text>
            </View>
            <Text
              style={[
                styles.metricLabel,
                {
                  color: patientMobilePalette.primaryText,
                  flex: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 13,
                },
              ]}
            >
              {remindersEnabled
                ? "Reminders enabled for this workspace"
                : "Reminders paused for this workspace"}
            </Text>
          </Pressable>
        {reminderRow ? (
          <View
            style={[styles.patientMobileSettingsRow, { gap: 10 }]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: patientMobilePalette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 13,
                  width: 82,
                },
              ]}
            >
              Default time
            </Text>
            <View
              style={styles.patientMedicationPreferenceSliderHost}
            >
              {renderPatientMedicationPreferenceSlider({
                activeColor: patientMobilePalette.actionBlue,
                label: "Default reminder time",
                maxMinutes: reminderRow.maxMinutes,
                minMinutes: reminderRow.minMinutes,
                minutes: reminderRow.minutes,
                onChange: (wakeUpTime) =>
                  onChangeReminderTime(reminderRow.id, wakeUpTime),
                onCommit: (wakeUpTime) =>
                  onUpdateReminderTime({
                    displayTime: formatPatientMobilePreferenceTime(wakeUpTime),
                    label: "Default",
                    slotId: reminderRow.id,
                    wakeUpTime,
                  }),
                trackColor: patientMobilePalette.softBlue,
              })}
            </View>
            <Text
              style={[
                styles.navigationText,
                {
                  color: patientMobilePalette.primaryText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 18,
                  fontWeight: "800",
                  width: 52,
                },
              ]}
            >
              {reminderRow.displayTime}
            </Text>
          </View>
        ) : null}
        {renderFactRow({
          label: "Notification channel",
          value: summary?.onboarding.preferences.reminderChannel === "portal"
            ? "In-app"
            : readProfileText(summary?.onboarding.preferences.reminderChannel),
        })}
      </View>
      <View
        style={[
          styles.patientMobilePanelCard,
          {
            backgroundColor: patientMobilePalette.white,
            borderColor: patientMobilePalette.line,
            borderRadius: designTokens.radius.md,
            gap: 14,
            padding: designTokens.spacing.lg,
          },
        ]}
      >
        <Text
          style={[
            styles.navigationText,
            {
              color: patientMobilePalette.primaryText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 14,
              fontWeight: "800",
            },
          ]}
        >
          Care team
        </Text>
        {careRows.map(renderFactRow)}
        <View style={[styles.patientRowFooter, { gap: 10 }]}>
          <Pressable
            onPress={onSignOut}
            style={[
              styles.patientMobileSettingsButton,
              {
                backgroundColor: patientMobilePalette.actionBlue,
                borderColor: patientMobilePalette.actionBlue,
                borderRadius: 6,
                flexGrow: 0,
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: patientMobilePalette.white,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 14,
                  fontWeight: "800",
                },
              ]}
            >
              Sign out
            </Text>
          </Pressable>
        </View>
        <Pressable
          onPress={() => onStartOnboarding()}
          style={[
            styles.patientMobileSettingsButton,
            {
              backgroundColor: patientMobilePalette.softBlue,
              borderColor: patientMobilePalette.line,
              borderRadius: 6,
              flexGrow: 0,
            },
          ]}
        >
          <Text
            style={[
              styles.navigationText,
              {
                color: patientMobilePalette.primaryText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 14,
                fontWeight: "800",
                textAlign: "center",
              },
            ]}
          >
            Launch onboarding guide
          </Text>
        </Pressable>
        {canSwitchWorkspaces ? (
          <Text
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.mutedText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
              },
            ]}
          >
            {`Active workspace: ${activeWorkforceSession?.displayName ?? summary?.displayName ?? "Patient"}`}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

export function renderPatientMobileChatScreen(input: {
  conversationMessages: ChatMessageViewModel[];
  draftValue: string;
  isMessageCommandSubmitting: boolean;
  layout: typeof designTokens.semanticLayout;
  noticeText: string | null;
  onConversationContentSizeChange: () => void;
  onDraftChange: (value: string) => void;
  onNavigateTab: (tabId: PatientWorkspaceTabId) => void;
  onOpenSupportPanel: (input: PatientSupportModalState) => void;
  onSendMessage: (input: PatientMessageCommandInput) => Promise<boolean>;
  patientConversationScrollRef: RefObject<ScrollView | null>;
  readAloudControls: PatientWorkspaceReadAloudControls;
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null;
  voiceInputControls: PatientWorkspaceVoiceInputControls;
}) {
  const {
    conversationMessages,
    draftValue,
    isMessageCommandSubmitting,
    layout,
    noticeText,
    onConversationContentSizeChange,
    onDraftChange,
    onNavigateTab,
    onOpenSupportPanel,
    onSendMessage,
    patientConversationScrollRef,
    readAloudControls,
    summary,
    voiceInputControls,
  } = input;
  const canSend = draftValue.trim().length > 0 && !isMessageCommandSubmitting;
  const sendMessage = () => {
    const bodyText = draftValue.trim();

    if (!bodyText) {
      return;
    }

    void onSendMessage({ bodyText }).then((wasApplied) => {
      if (wasApplied) {
        onDraftChange("");
      }
    });
  };

  return (
    <View
      style={[
        styles.patientMobileChatRoot,
        { backgroundColor: patientMobilePalette.body },
      ]}
    >
      <View
        style={[
          styles.patientMobileChatIdentity,
          createWebGradientBackgroundStyle(patientMobilePalette.primaryGradient),
          { backgroundColor: patientMobilePalette.darkBlue },
        ]}
      >
        {renderPatientMobileChatAvatarMedia()}
        <View style={[styles.sectionChromeCopy, { gap: 1 }]}>
          <Text
            style={[
              styles.sectionTitle,
              {
                color: patientMobilePalette.white,
                fontFamily: layout.displayFontFamily,
                fontSize: 18,
                lineHeight: 22,
              },
            ]}
          >
            {brand.assistantName}
          </Text>
          <Text
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.footerMuted,
                fontFamily: layout.bodyFontFamily,
                fontSize: 11,
              },
            ]}
          >
            Care support
          </Text>
        </View>
        <Pressable
          onPress={readAloudControls.toggleAutoReadAloud}
          style={styles.patientMobileReadAloudButton}
        >
          <Text
            style={[
              styles.metricLabel,
              {
                color: patientMobilePalette.white,
                fontFamily: layout.bodyFontFamily,
                fontSize: 11,
                fontWeight: "700",
              },
            ]}
          >
            Read aloud
          </Text>
        </Pressable>
        <Pressable
          onPress={() => onNavigateTab("overview")}
          style={[
            styles.patientMobileChatClose,
            { borderColor: patientMobilePalette.white, borderRadius: 18 },
          ]}
        >
          <Text
            style={[
              styles.navigationText,
              {
                color: patientMobilePalette.white,
                fontFamily: layout.bodyFontFamily,
                fontSize: 18,
                fontWeight: "800",
              },
            ]}
          >
            X
          </Text>
        </Pressable>
      </View>
      {noticeText ? (
        <Text
          style={[
            styles.metricLabel,
            {
              color: patientMobilePalette.mutedText,
              fontFamily: layout.bodyFontFamily,
              fontSize: 12,
              paddingHorizontal: designTokens.spacing.lg,
              paddingTop: designTokens.spacing.sm,
              textAlign: "center",
            },
          ]}
        >
          {noticeText}
        </Text>
      ) : null}
      <ScrollView
        ref={patientConversationScrollRef}
        onContentSizeChange={onConversationContentSizeChange}
        style={styles.patientMobileChatScroll}
        contentContainerStyle={[
          styles.patientMobileChatContent,
          { gap: designTokens.spacing.sm, padding: 14 },
        ]}
      >
        {conversationMessages.length > 0 ? (
          conversationMessages.map((message) => {
            const isPatient = message.senderKind === "patient";
            const bubbleColor = isPatient
              ? patientMobilePalette.actionBlue
              : patientMobilePalette.white;
            const textColor = isPatient
              ? patientMobilePalette.white
              : patientMobilePalette.primaryText;

            return (
              <View
                key={message.id}
                style={[
                  styles.patientMobileChatBubble,
                  {
                    alignSelf: isPatient ? "flex-end" : "flex-start",
                    backgroundColor: bubbleColor,
                    borderColor: isPatient
                      ? patientMobilePalette.actionBlue
                      : patientMobilePalette.line,
                    borderRadius: designTokens.radius.md,
                    maxWidth: "88%",
                    padding: 10,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: textColor,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 13,
                      lineHeight: 18,
                    },
                  ]}
                >
                  {message.bodyText}
                </Text>
                <View style={[styles.patientRowFooter, { gap: designTokens.spacing.sm }]}>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: isPatient ? patientMobilePalette.footerMuted : patientMobilePalette.mutedText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                      },
                    ]}
                  >
                    {message.timestampText}
                  </Text>
                  {!isPatient ? (
                    <>
                      <Pressable
                        onPress={() => readAloudControls.readMessageAloud(message)}
                      >
                        <Text
                          style={[
                            styles.metricLabel,
                            {
                              color: patientMobilePalette.actionBlue,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 10,
                              fontWeight: "800",
                            },
                          ]}
                        >
                          Read answer
                        </Text>
                      </Pressable>
                      <Pressable
                        onPress={() =>
                          onOpenSupportPanel({
                            kind: "chat-feedback",
                            message,
                            source: "patient-mobile-chat:send-feedback",
                          })
                        }
                      >
                        <Text
                          style={[
                            styles.metricLabel,
                            {
                              color: patientMobilePalette.actionBlue,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 10,
                              fontWeight: "800",
                            },
                          ]}
                        >
                          Send feedback
                        </Text>
                      </Pressable>
                    </>
                  ) : null}
                </View>
              </View>
            );
          })
        ) : (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: patientMobilePalette.mutedText,
                fontFamily: layout.bodyFontFamily,
                fontSize: 13,
                lineHeight: 18,
              },
            ]}
          >
            {`Hi there. I am your ${brand.assistantName}. How can I assist you`}
            today?
          </Text>
        )}
      </ScrollView>
      <View
        style={[
          styles.patientMobileComposer,
          {
            backgroundColor: patientMobilePalette.body,
            borderTopColor: patientMobilePalette.line,
            gap: designTokens.spacing.sm,
            padding: 10,
          },
        ]}
      >
        <View
          style={[
            styles.patientMobileComposerInput,
            {
              backgroundColor: patientMobilePalette.white,
              borderColor: patientMobilePalette.line,
              borderRadius: designTokens.radius.md,
            },
          ]}
        >
          <TextInput
            onChangeText={onDraftChange}
            onSubmitEditing={sendMessage}
            placeholder="Type message..."
            placeholderTextColor={patientMobilePalette.mutedText}
            style={[
              styles.searchInput,
              {
                color: patientMobilePalette.primaryText,
                flex: 1,
                fontFamily: layout.bodyFontFamily,
                fontSize: 16,
              },
            ]}
            value={draftValue}
          />
          <Pressable onPress={voiceInputControls.toggleListening}>
            <LineIcon
              color={patientMobilePalette.primaryText}
              name="microphone"
              size={18}
              strokeWidth={2.4}
            />
          </Pressable>
        </View>
        <Pressable
          disabled={!canSend}
          onPress={sendMessage}
          style={[
            styles.patientMobileSendCircle,
            {
              backgroundColor: patientMobilePalette.actionBlue,
              borderColor: patientMobilePalette.actionBlue,
              borderRadius: 18,
              opacity: canSend ? 1 : 0.6,
            },
          ]}
        >
          {isMessageCommandSubmitting ? (
            <Text
              style={[
                styles.navigationText,
                {
                  color: patientMobilePalette.white,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 14,
                  fontWeight: "800",
                },
              ]}
            >
              ...
            </Text>
          ) : (
            <LineIcon
              color={patientMobilePalette.white}
              name="chevron-right"
              size={18}
              strokeWidth={2.5}
            />
          )}
        </Pressable>
      </View>
    </View>
  );
}

export function formatPatientMobilePreferenceTime(value: string | null | undefined) {
  const trimmedValue = value?.trim() ?? "";
  const match = /^(\d{1,2}):(\d{2})$/.exec(trimmedValue);

  if (!match) {
    return trimmedValue.length > 0 ? trimmedValue : "7 AM";
  }

  const hour24 = Number(match[1]);
  const minutes = match[2];
  const suffix = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

  return minutes === "00" ? `${hour12} ${suffix}` : `${hour12}:${minutes} ${suffix}`;
}
